import React, { Suspense, lazy, useRef, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { PropTypes } from 'prop-types';
import { getPathName, isImzFlowType } from './common/utils';
import loading from './common/images/loading.gif';
import Header from './layout/Header/Header';
import getContextPath from './GlobalVariable';
import './App.scss';
import UnSubscribePage from './components/UnSubscribeForm/Unsubscribe';
import NoCoupons from './components/NoCoupon/NoCoupon';
import ImzDeprecationPage from './components/ImzDeprecationPage';

const incentiveURL = import('./components/IncentiveForm/IncentiveForm');
const unSubscribeURL = import('./components/UnSubscribeForm/Unsubscribe');
const noPharmacyURL = import('./components/NoPharmacyOffer/NoPharmacyOffer');
const pharmacyURL = import('./components/PharmacyOffer/PharmacyOffer');
const govtFundedURL = import(
  './components/CustomerGovtFundedInsurance/CustomerGovtFundedInsurance'
);
const SuccessAcknowledgementURL = import(
  './components/SuccessAcknowledgement/SuccessAcknowledgement'
);
const newLoyaltyURL = import(
  './components/NewLoyaltyCreation/NewLoyaltyCreation'
);
const registrationModalURL = import(
  './components/NewLoyaltyCreation/RegistrationModal/RegistrationModal'
);
const consentFormURL = import(
  './components/PharmacyOffer/ConsentForm/ConsentForm'
);
const PrescriptionFillsCountURl = import(
  './components/PrescriptionFillsCount/PrescriptionFillsCount'
);
const OfferConfirmationURl = import(
  './components/OfferConfirmation/OfferConfirmation'
);
const NoOfferURl = import('./components/NoOffer/NoOffer');
const genericErrorURl = import('./components/GenericError/GenericError');

const GenericError = lazy(() => genericErrorURl);
const ErroPgeComponent = import('./components/ErrorPage/ErrorPage');
const HipaaConsentURL = import('./components/HipaaConsent/HipaaConsent');

const ErrorPage = lazy(() => ErroPgeComponent);
const IncentiveForm = lazy(() => incentiveURL);
const UnsubscribeForm = lazy(() => unSubscribeURL);
const NoPharmacyOffer = lazy(() => noPharmacyURL);
const UserInfo = lazy(() => import('./components/UserInfo/UserInfo'));
const PharmacyOffer = lazy(() => pharmacyURL);
const CustomerGovtFundedInsurance = lazy(() => govtFundedURL);
const SuccessAcknowlegement = lazy(() => SuccessAcknowledgementURL);
const NewLoyaltyCreation = lazy(() => newLoyaltyURL);
const RegistrationModal = lazy(() => registrationModalURL);
const ConsentForm = lazy(() => consentFormURL);
const PrescriptionFillsCount = lazy(() => PrescriptionFillsCountURl);

const OfferConfirmation = lazy(() => OfferConfirmationURl);
const NoOffer = lazy(() => NoOfferURl);
const HipaaConsent = lazy(() => HipaaConsentURL);

function ErrorFallback({ error }) {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre style={{ color: 'red' }}>{error.message}</pre>
    </div>
  );
}

function App() {
  const paramPathValue = getPathName();
  const isUnsubscribe = paramPathValue.includes('unsubscribe');
  const wrapperRef = useRef(null);

  const browserUrl = window.location.href;
  const envUrl = browserUrl.split('/')[2];

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://${envUrl}/rx-analytics-web/magicbox.js`;
    script.async = true;
    script.id = 'magicbox';
    script.type = 'text/javascript';
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const renderOtherPage = () => {
    if (isUnsubscribe) {
      return <UnSubscribePage />;
    }
    if (isImzFlowType) {
      return <ImzDeprecationPage />;
    }
    return null;
  };

  return (
    <div className="incentive-app">
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        {getContextPath() && (
          <>
            {' '}
            <Suspense
              fallback={
                <img
                  src={loading}
                  alt="loading"
                  className="loadingImage"
                  aria-live="polite"
                />
              }
            >
              <Router basename={getContextPath()}>
                {renderOtherPage() ? (
                  renderOtherPage()
                ) : (
                  <div>
                    <Header />
                    <main>
                      <div className="row incentive-form-conatiner">
                        <div className="section-wrapper">
                          <section
                            ref={wrapperRef}
                            className="container-wrapper mb-3"
                            data-testid="container-wrapper"
                          >
                            <Routes>
                              <Route
                                path="/*"
                                element={<Navigate replace to="/" />}
                              />
                              <Route path="/" element={<IncentiveForm />} />
                              <Route path="userinfo" element={<UserInfo />} />
                              <Route
                                path="unsubscribe"
                                element={<UnsubscribeForm />}
                              />

                              <Route
                                path="genericerror"
                                element={<GenericError />}
                              />
                              <Route
                                path="prescriptionfillscount"
                                element={<PrescriptionFillsCount />}
                              />
                              <Route
                                path="offerconfirmation"
                                element={<OfferConfirmation ref={wrapperRef} />}
                              />
                              <Route
                                path="pharmacyoffer"
                                element={<PharmacyOffer />}
                              />
                              <Route
                                path="hipaaconsent"
                                element={<HipaaConsent />}
                              />
                              <Route
                                path="govtfunded"
                                element={<CustomerGovtFundedInsurance />}
                              />
                              <Route
                                path="nopharmacyoffer"
                                element={<NoPharmacyOffer />}
                              />
                              <Route path="nooffer" element={<NoOffer />} />
                              <Route
                                path="newloyaltycreation"
                                element={<NewLoyaltyCreation />}
                              />
                              <Route
                                path="registrationmodal"
                                element={<RegistrationModal />}
                              />
                              <Route
                                path="consentform"
                                element={<ConsentForm />}
                              />
                              <Route path="errorpage" element={<ErrorPage />} />
                              <Route
                                path="successacknowlegement"
                                element={<SuccessAcknowlegement />}
                              />
                              <Route path="nocoupon" element={<NoCoupons />} />
                            </Routes>
                          </section>
                        </div>
                      </div>
                    </main>
                  </div>
                )}
              </Router>
            </Suspense>
          </>
        )}
      </ErrorBoundary>
    </div>
  );
}
ErrorFallback.propTypes = {
  error: PropTypes.any,
};
export default App;
export { ErrorFallback };
